import { useMutation } from "@tanstack/react-query";
import { format } from "date-fns";
import { Mail, Send } from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import useAppHistory from "@/hooks/useAppHistory";
import printedDocumentEmailApi from "@/types/printedDocumentEmail/printedDocumentEmailApi";
import mutate from "@/Utils/request/mutate";

import { generatePrintElementPdf } from "./printPdf";

interface PrintEmailDialogProps {
  documentTitle: string;
  disabled?: boolean;
  defaultOpen?: boolean;
  patientId?: string | null;
  patientEmail?: string | null;
  patientName?: string | null;
  documentDateTime?: string | Date | null;
  printableElementId: string;
}

export default function PrintEmailDialog({
  documentTitle,
  disabled,
  defaultOpen,
  patientId,
  patientEmail,
  patientName,
  documentDateTime,
  printableElementId,
}: PrintEmailDialogProps) {
  const { t } = useTranslation();
  const { goBack } = useAppHistory();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(patientEmail ?? "");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [isPreparingPdf, setIsPreparingPdf] = useState(false);

  const formattedDocumentDateTime = useMemo(() => {
    const date = documentDateTime ? new Date(documentDateTime) : new Date();
    if (Number.isNaN(date.getTime())) {
      return format(new Date(), "dd MMM yyyy, h:mm a");
    }
    return format(date, "dd MMM yyyy, h:mm a");
  }, [documentDateTime]);

  const defaultSubject = useMemo(
    () =>
      t("printed_document_email_subject", {
        title: documentTitle,
        datetime: formattedDocumentDateTime,
      }),
    [documentTitle, formattedDocumentDateTime, t],
  );
  const defaultMessage = useMemo(
    () =>
      t("printed_document_email_message", {
        title: documentTitle,
        patient: patientName || t("patient"),
      }),
    [documentTitle, patientName, t],
  );

  useEffect(() => {
    if (defaultOpen && !disabled) {
      setOpen(true);
    }
  }, [defaultOpen, disabled]);

  useEffect(() => {
    if (!open) return;
    setEmail(patientEmail ?? "");
    setSubject(defaultSubject);
    setMessage(defaultMessage);
  }, [defaultMessage, defaultSubject, open, patientEmail]);

  const { mutateAsync: sendPrintedDocument, isPending } = useMutation({
    mutationFn: mutate(printedDocumentEmailApi.send),
  });

  const isSending = isPreparingPdf || isPending;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsPreparingPdf(true);
    try {
      const pdf = await generatePrintElementPdf(
        printableElementId,
        documentTitle,
      );
      const formData = new FormData();
      formData.append("to_email", email.trim());
      formData.append("subject", subject.trim());
      formData.append("message", message.trim());
      formData.append("document_title", documentTitle);
      if (patientId) {
        formData.append("patient_id", patientId);
      }
      formData.append("attachment", pdf);

      await sendPrintedDocument(formData);
      toast.success(t("printed_document_email_queued"));
      setOpen(false);
      if (defaultOpen) {
        goBack();
      }
    } catch {
      toast.error(t("printed_document_email_failed"));
    } finally {
      setIsPreparingPdf(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        disabled={disabled || !patientId}
        onClick={() => setOpen(true)}
      >
        <Mail className="size-4" />
        {t("email")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("email_printed_document")}</DialogTitle>
            <DialogDescription>
              {patientEmail
                ? t("printed_document_email_existing_patient_email", {
                    email: patientEmail,
                  })
                : patientId
                  ? t("printed_document_email_missing_patient_email")
                  : t("printed_document_email_generic_recipient")}
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <Label htmlFor="printed-document-email-to">
                {t("email_address")}
              </Label>
              <Input
                id="printed-document-email-to"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder={t("example_email_address")}
                required
                disabled={isSending}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="printed-document-email-subject">
                {t("subject")}
              </Label>
              <Input
                id="printed-document-email-subject"
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                required
                disabled={isSending}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="printed-document-email-message">
                {t("message")}
              </Label>
              <Textarea
                id="printed-document-email-message"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                disabled={isSending}
              />
            </div>
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
              {t("printed_document_email_attachment_notice", {
                title: documentTitle,
              })}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={isSending}
                onClick={() => setOpen(false)}
              >
                {t("cancel")}
              </Button>
              <Button type="submit" disabled={isSending}>
                {isSending ? (
                  <span className="size-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                ) : (
                  <Send className="size-4" />
                )}
                {isSending ? t("sending") : t("send")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
